import { MachineServices } from 'common/bento/types/MachineConfig';
import { apolloClient } from 'common/graphql/clients';
import webView from 'common/webView';
import logger from 'helpers/logger';

import { NativeFourthlineQesOutcome } from '../../Identity/machine/model';
import { FinishQesDocument } from '../graphql/mutations/finishQes.gql';
import { StartQesDocument } from '../graphql/mutations/startQes.gql';
import { GetIdentityAndQesStatusDocument } from '../graphql/queries/getIdentityAndQesStatus.gql';
import { QesModuleDataDocument } from '../graphql/queries/qesModuleData.gql';
import { Model, SignatureContext } from './model';

export enum Service {
  StartSignature = 'startSignature',
  FinishSignature = 'finishSignature',
  CompleteNativeFourthlineWorkflow = 'completeNativeFourthlineWorkflow',
}

const services: MachineServices<Model, Service> = {
  completeNativeFourthlineWorkflow: async ({
    signatureContext,
  }): Promise<NativeFourthlineQesOutcome> => {
    if (!signatureContext?.token) {
      throw new Error(
        `No Fourthline token in QES completeNativeFourthlineWorkflow`,
      );
    }

    webView.postMessage({
      action: 'NATIVE_FOURTHLINE_QES_REQUESTED',
      payload: {
        verificationCode: signatureContext.token,
      },
    });

    return new Promise((resolve) => {
      webView.listenNativeMessage(async (message) => {
        switch (message.action) {
          case 'NATIVE_ID_CHECK_COMPLETED': {
            await apolloClient.refetchQueries({
              include: [QesModuleDataDocument],
            });

            return resolve({ status: 'done' });
          }

          case 'NATIVE_FOURTHLINE_QES_FAILED': {
            const fourthlineErrorCode = message.payload.errorCode;

            if (!fourthlineErrorCode) {
              return resolve({ error: null, status: 'error' });
            }

            if (fourthlineErrorCode === 803) {
              return resolve({ status: 'canceled' });
            }

            return resolve({ error: fourthlineErrorCode, status: 'error' });
          }

          default: {
            logger.error(
              `Unhandled message in NativeFourthlineQes: ${JSON.stringify(
                message,
              )}`,
            );
            await apolloClient.refetchQueries({
              include: [QesModuleDataDocument],
            });

            /**
             * By default we'll try to mark Fourthline as done.
             * Refetching the data will show to the user if it was really done, or not.
             */
            return resolve({ status: 'done' });
          }
        }
      });
    });
  },

  finishSignature: async ({ getModuleData }) => {
    const { companyUserId } = getModuleData().viewer.companyUser;
    return apolloClient.mutate({
      mutation: FinishQesDocument,
      variables: {
        input: {
          companyUserId,
        },
      },
    });
  },

  startSignature: async ({ getModuleData }): Promise<SignatureContext> => {
    const { companyProfileId, viewer } = getModuleData();
    const { companyUserId } = viewer.companyUser;

    try {
      const { data: startQesData } = await apolloClient.mutate({
        mutation: StartQesDocument,
        variables: {
          input: {
            companyUserId,
          },
        },
      });

      if (!startQesData?.startQes.sdkToken) {
        throw new Error(`Missing sdkToken in startSignature`);
      }

      return {
        token: startQesData.startQes.sdkToken,
      };
    } catch (error) {
      await apolloClient.query({
        fetchPolicy: 'network-only',
        query: GetIdentityAndQesStatusDocument,
        variables: {
          companyProfileId,
        },
      });

      throw error;
    }
  },
};

export default services;

export type Services = typeof services;
